import React, { useEffect, useMemo, useRef, useState } from 'react';
import { authAPI } from '../config/api';
import { showAppToast } from '../utils/notifications';

const emptyForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

function getErrorMessage(error) {
  const payload = error.response?.data;
  const details = payload?.data;
  if (details && typeof details === 'object') {
    const firstDetail = Object.values(details).flat()[0];
    if (firstDetail) return String(firstDetail);
  }
  return payload?.message || 'Password could not be changed. Please try again.';
}

function AccountSecurityModal({ isOpen, onClose, accountName = 'your account' }) {
  const [form, setForm] = useState(emptyForm);
  const [showPasswords, setShowPasswords] = useState(false);
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef(null);
  const currentPasswordRef = useRef(null);

  const passwordChecks = useMemo(() => [
    { label: 'At least 8 characters', met: form.newPassword.length >= 8 },
    { label: 'One uppercase letter', met: /[A-Z]/.test(form.newPassword) },
    { label: 'One lowercase letter', met: /[a-z]/.test(form.newPassword) },
    { label: 'One number', met: /\d/.test(form.newPassword) },
  ], [form.newPassword]);

  const isValid = form.currentPassword
    && passwordChecks.every((check) => check.met)
    && form.newPassword !== form.currentPassword
    && form.newPassword === form.confirmPassword;

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.setTimeout(() => currentPasswordRef.current?.focus(), 0);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) onClose();
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled)');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, saving]);

  useEffect(() => {
    if (!isOpen) {
      setForm(emptyForm);
      setShowPasswords(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const changeField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!isValid || saving) return;
    setSaving(true);
    try {
      await authAPI.changePassword(form);
      showAppToast({ message: 'Your password has been changed securely.', type: 'success' });
      onClose();
    } catch (error) {
      showAppToast({ message: getErrorMessage(error), type: 'error', duration: 4200 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="account-security-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}
    >
      <section
        ref={dialogRef}
        className="account-security-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-security-title"
      >
        <header className="account-security-header">
          <div className="account-security-icon" aria-hidden="true"><i className="bi bi-key-fill" /></div>
          <div>
            <p>Account security</p>
            <h2 id="account-security-title">Change password</h2>
            <span>Update the sign-in password for {accountName}.</span>
          </div>
          <button type="button" className="account-security-close" onClick={onClose} disabled={saving} aria-label="Close password dialog">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </header>

        <form className="account-security-form" onSubmit={submit}>
          <label className="account-security-field">
            <span>Current password</span>
            <input
              ref={currentPasswordRef}
              type={showPasswords ? 'text' : 'password'}
              autoComplete="current-password"
              value={form.currentPassword}
              onChange={(event) => changeField('currentPassword', event.target.value)}
              required
            />
          </label>

          <label className="account-security-field">
            <span>New password</span>
            <input
              type={showPasswords ? 'text' : 'password'}
              autoComplete="new-password"
              value={form.newPassword}
              onChange={(event) => changeField('newPassword', event.target.value)}
              minLength={8}
              required
            />
          </label>

          <label className="account-security-field">
            <span>Confirm new password</span>
            <input
              type={showPasswords ? 'text' : 'password'}
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={(event) => changeField('confirmPassword', event.target.value)}
              minLength={8}
              required
              aria-invalid={Boolean(form.confirmPassword && form.confirmPassword !== form.newPassword)}
            />
            {form.confirmPassword && form.confirmPassword !== form.newPassword && (
              <small className="account-security-error">Passwords do not match.</small>
            )}
          </label>

          <label className="account-security-show">
            <input type="checkbox" checked={showPasswords} onChange={(event) => setShowPasswords(event.target.checked)} />
            <span>Show passwords</span>
          </label>

          <div className="account-security-checks" aria-label="Password requirements">
            {passwordChecks.map((check) => (
              <span key={check.label} className={check.met ? 'met' : ''}>
                <i className={`bi ${check.met ? 'bi-check-circle-fill' : 'bi-circle'}`} aria-hidden="true" />
                {check.label}
              </span>
            ))}
          </div>

          <div className="account-security-actions">
            <button type="button" className="btn-luxury-outline" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-luxury" disabled={!isValid || saving}>
              <i className="bi bi-lock-fill" aria-hidden="true" />
              {saving ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default AccountSecurityModal;
